import React, { useState, useEffect, useCallback, useRef } from 'react';
import css from './MainDashboardWrapper.css';
import Collapse from '@kunukn/react-collapse';
import {
    Menu as MenuIcon,
    Grid,
    FileText,
    ChevronDown,
    ChevronUp,
    Calendar,
    Volume2,
    Mail,
    Bell,
    PieChart,
    ChevronLeft,
    Users,
    User,
    UserPlus,
    CreditCard,
    Settings,
    List,
    Search,
    Video, Award, Image, Shield, Lock, Folder, X, ArrowRight, Home} from 'react-feather';
import { meetsTierGate, TIERS } from '../../util/coachTier';
import { getLogoForRoute, coachLockupLogo } from '../../assets/viveka-logos';
import { Link, withRouter } from 'react-router-dom';
import config from '../../config';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Menu, MenuItem, SubMenu } from '@szhsin/react-menu';
import { logout } from '../../ducks/Auth.duck';
import defaultProfilePic from '../../assets/default_profile.png';
import { sendVerificationEmail, fetchCurrentUser } from '../../ducks/user.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import ReportIssueButton from '../ReportIssueButton/ReportIssueButton';
import { notifySuccess } from '../HotToast/HotToastProvider';
import { timeAgo } from '../../util/dateDisplay';
import NotificationTooltip, {
  NOTIFICATION_TOOLTIP_STORAGE_PREFIX,
} from '../../custom-components/elements/notification-tooltip/NotificationTooltip';
import SubscriptionExpiryBanner from '../../custom-components/elements/subscription-expiry-banner/SubscriptionExpiryBanner';
import InstallmentPaymentFailedBanner from '../../custom-components/elements/installment-payment-failed-banner/InstallmentPaymentFailedBanner';
import SubscriptionMigrationBanner from '../../custom-components/elements/subscription-migration-banner/SubscriptionMigrationBanner';
import SubscribeToBeVisibleBanner from '../../custom-components/elements/subscribe-to-be-visible-banner/SubscribeToBeVisibleBanner';
import SubscribeToBeVisiblePopup from '../../custom-components/elements/subscribe-to-be-visible-popup/SubscribeToBeVisiblePopup';
import ProfileCompletenessChecklist from '../../custom-components/elements/profile-completeness-checklist/ProfileCompletenessChecklist';
import VCoinCelebrationController from '../../custom-components/elements/vcoin-celebration/VCoinCelebrationController';
import CoachOnboardingTour from '../../custom-components/elements/coach-onboarding-tour/CoachOnboardingTour';
import { fetchPendingEnquiriesCount, getAllClients } from '../../custom-ducks/clients.duck';
import { fetchMyTestimonials } from '../../custom-ducks/testimonials.duck';
import { fetchAllMessages } from '../../custom-ducks/socketChat.duck';
import { getAllActivities } from '../../custom-ducks/activities.duck';
import { fetchMyResources } from '../../custom-ducks/resources.duck';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../custom-ducks/notifications.duck';

// Bell-dropdown icon per notification `type`. Falls back to a plain bell for
// any type not listed here (new notification types added later don't need
// an update here to render sensibly).
const NOTIFICATION_ICON_BY_TYPE = {
  client_invitation: UserPlus,
};

export const MainDashboardWrapper = props => {
  const {
    currentUser,
    children,
    title,
    page,
    history,
    location,
    logout,
    onResendVerificationEmail,
    onFetchCurrentUser,
    currentUserNotificationCount,
    breadcrumbItems = [],
    currentUserHasOrders,
    unreadMessageCount,
    onFetchPendingEnquiriesCount,
    stripeAccount,
    onFetchStripeAccount,
    hideSidebar = false,
    forceCoachView = false,
    hideTitleOnMobile = false,
    clients = [],
    onFetchClients,
    clientsFetching = false,
    testimonials = [],
    onFetchTestimonials,
    testimonialsFetching = false,
    activities = [],
    onGetAllActivities,
    activitiesFetching = false,
    onFetchAllMessages,
    resources = [],
    onFetchResources,
    resourcesFetching = false,
    notificationItems = [],
    notificationUnreadCount = 0,
    onFetchNotifications,
    onMarkNotificationRead,
    onMarkAllNotificationsRead,
  } = props;

  const [isVCorpOpen, setIsVCorpOpen] = useState(true);
  const [isNavOpen, setIsNavOpen] = useState(false);
  // Lazy init so a participant's first paint already shows participant
  // styling/sections instead of flashing coach state (the old hardcoded
  // 'coach' default) — needed now that the coach section/switch button are
  // no longer restricted to isProvider accounts.
  const [activeView, setActiveView] = useState(() => {
    if (forceCoachView) return 'coach';
    return currentUser?.attributes?.profile?.publicData?.isProvider ? 'coach' : 'participant';
  });

  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  const currentCompany =
    currentUser?.attributes?.profile?.metadata[
      `currentCompany${process.env.REACT_APP_ENVIRONMENT}`
    ];
  const currentCompanyRoles = currentCompany?.roles || [];
  // An "organization" user is an HR/admin of a V-Corp company account —
  // same condition that already gated the Organization sidebar section.
  // These users get a sidebar showing ONLY Organization (Coach/Participant
  // hidden), with a Coach/Organization switch when they're also a coach.
  const isOrganization = !!(
    currentCompany &&
    (currentCompanyRoles.includes('admin') || currentCompanyRoles.includes('hr'))
  );

  // Once the current user is known, derive the correct default view from their
  // role. Organization admins start in organization view; coaches start in
  // coach view; everyone else defaults to participant view. All three respect
  // any per-user stored preference. Using a per-user key prevents a stale
  // preference from a previous session bleeding into a fresh login.
  useEffect(() => {
    if (!currentUser?.id?.uuid) return;
    const isCoach = !!currentUser.attributes?.profile?.publicData?.isProvider;
    const stored = localStorage.getItem(`viveka_activeView_${currentUser.id.uuid}`);
    if (stored) {
      setActiveView(stored);
      return;
    }
    if (isOrganization) {
      setActiveView('organization');
    } else if (!isCoach) {
      setActiveView('participant');
    } else {
      setActiveView('coach');
    }
  }, [currentUser?.id?.uuid, isOrganization]);

  // Keep the sidebar section in sync with whatever route is actually being
  // viewed. Without this, opening a direct link/bookmark to e.g. a coach
  // page while activeView was last left on 'organization' or 'participant'
  // (from a previous visit) would navigate correctly but leave the sidebar
  // showing the wrong section. Organization has no matching route here —
  // its pages live on the separate V-Corp app — so only coach/participant
  // routes drive this; anything else (marketplace, shared inbox, etc.)
  // leaves activeView untouched.
  useEffect(() => {
    if (!currentUser?.id?.uuid) return;
    const path = location?.pathname || '';
    let derivedView = null;
    if (path.startsWith('/v-coach/client/') || path.startsWith('/saas-chats/client')) {
      derivedView = 'participant';
    } else if (path.startsWith('/v-coach/') || path.startsWith('/saas-chats/coach')) {
      derivedView = 'coach';
    }
    if (!derivedView) return;
    setActiveView(prev => {
      if (derivedView === prev) return prev;
      localStorage.setItem(`viveka_activeView_${currentUser.id.uuid}`, derivedView);
      return derivedView;
    });
  }, [location?.pathname, currentUser?.id?.uuid]);

  const switchView = (view) => {
    if (currentUser?.id?.uuid) {
      localStorage.setItem(`viveka_activeView_${currentUser.id.uuid}`, view);
    }
    setActiveView(view);
    if (view === 'organization') {
      // Organization pages live on the separate V-Corp app, not as routes
      // in this app — same target as the Organization section's own
      // Dashboard link, so the switch button behaves consistently with it.
      window.location.href = `${config.vCorpUrl}/hr/dashboard`;
      return;
    }
    history.push(view === 'coach' ? '/v-coach/dashboard' : '/v-coach/client/dashboard');
  };
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  const [showStripePopup, setShowStripePopup] = useState(false);
  // SubscribeToBeVisiblePopup reports its own open state here so the coach
  // onboarding tooltip tour (below) can wait until the paywall modal is
  // dismissed instead of stacking on top of it.
  const [subscribePopupOpen, setSubscribePopupOpen] = useState(false);

  // Anchors for the coach onboarding tooltip tour (Phase 1: profile photo,
  // Stripe payout, calendar availability; Phase 2: program, ad; Phase 3:
  // clients, testimonials; Phase 4: messaging, booking, payments; Phase 5:
  // resource library) — see CoachOnboardingTour.
  const profileMenuAnchorRef = useRef(null);
  const settingsLinkAnchorRef = useRef(null);
  const programAnchorRef = useRef(null);
  const adsAnchorRef = useRef(null);
  const clientsAnchorRef = useRef(null);
  const messagesAnchorRef = useRef(null);
  const sessionsAnchorRef = useRef(null);
  const resourcesAnchorRef = useRef(null);
  const notificationsBellAnchorRef = useRef(null);

  const emailVerifiedRef = React.useRef(currentUser?.attributes?.emailVerified);
  emailVerifiedRef.current = currentUser?.attributes?.emailVerified;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && emailVerifiedRef.current === false) {
        onFetchCurrentUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [onFetchCurrentUser]);

  useEffect(() => {
    if (
      currentUser?.attributes?.stripeConnected &&
      page?.startsWith('coach') &&
      !stripeAccount?.attributes?.stripeAccountData
    ) {
      onFetchStripeAccount();
    }
  }, [currentUser, page]);

  // Viveka admin flag — exposed to the sidebar so the admin nav entry
  // only renders for actual admins. Source of truth is still the
  // backend adminAuth middleware; this is just a UI nicety so the link
  // doesn't sit there for non-admin coaches. Two paths (matches backend):
  //   • REACT_APP_VIVEKA_ADMIN_USER_IDS env var (comma-separated UUIDs)
  //   • profile.metadata.role === 'viveka_admin'
  const __adminEnvIds = (process.env.REACT_APP_VIVEKA_ADMIN_USER_IDS || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const __currentUid = currentUser?.id?.uuid;
  const isVivekaAdmin = !!(
    (__currentUid && __adminEnvIds.includes(__currentUid)) ||
    currentUser?.attributes?.profile?.metadata?.role === 'viveka_admin'
  );

  // Wave 1 plan-tier gate — Reports is a Verified-tier feature.
  // Sidebar link still renders for Network coaches but with a lock
  // icon + muted style; clicking lands on the inline UpgradeGate.
  const isVerifiedPlus = meetsTierGate(currentUser, TIERS.VERIFIED);

  const stripeConnectedFlag = !!currentUser?.attributes?.stripeConnected;
  const stripeAccountData = stripeAccount?.attributes?.stripeAccountData || null;
  const stripeHasRequirements =
    stripeAccountData?.requirements &&
    (
      (Array.isArray(stripeAccountData.requirements.past_due) && stripeAccountData.requirements.past_due.length > 0) ||
      (Array.isArray(stripeAccountData.requirements.currently_due) && stripeAccountData.requirements.currently_due.length > 0)
    );
  const stripeVerified = stripeConnectedFlag && !stripeHasRequirements;

  const requireStripe = (to) => {
    if (!stripeVerified) {
      setShowStripePopup(true);
    } else {
      history.push(to);
    }
  };

  // Always surface the Participant area. A coach should be able to act as a
  // client of other coaches (browse, book, and manage their own sessions)
  // even before their first purchase. This was previously gated on
  // currentUserHasOrders, which hid the entire Participant section from any
  // coach who had not yet booked someone — so a coach could never get
  // started as a participant from the dashboard. currentUserHasOrders /
  // currentCompanyRoles are still read above for other UI (logo, V-Corp
  // section, company curriculum link).
  const showVClient = true;

  // Pending booking-enquiries count for the Bell. Sharetribe sales in
  // transition/enquire OR transition/confirm-payment.
  useEffect(() => {
    if (!isProvider || !onFetchPendingEnquiriesCount) return;
    onFetchPendingEnquiriesCount()
      .then(count => setPendingInvitesCount(count || 0))
      .catch(() => setPendingInvitesCount(0));
  }, [isProvider, onFetchPendingEnquiriesCount]);

  // In-app bell notifications (e.g. "Coach X invited you") — generic
  // per-user feed, works for any role. Fetched once the account is known,
  // then polled every 30s so a newly-received invitation shows up in the
  // bell on its own — previously this only fetched once per login, so a
  // participant already sitting on the dashboard had to reload the page to
  // see a notification that arrived after that fetch.
  useEffect(() => {
    if (!currentUser?.id?.uuid || !onFetchNotifications) return;
    onFetchNotifications().catch(() => {});
    const intervalId = setInterval(() => {
      onFetchNotifications().catch(() => {});
    }, 30000);
    return () => clearInterval(intervalId);
  }, [currentUser?.id?.uuid, onFetchNotifications]);

  // Clients/testimonials counts feed the Phase 3 onboarding tooltip below
  // (CoachOnboardingTour) — it needs to know whether the coach has added at
  // least one of each, and there's no boolean flag for that on currentUser.
  useEffect(() => {
    if (!isProvider) return;
    if (onFetchClients) onFetchClients();
    if (onFetchTestimonials) onFetchTestimonials();
  }, [isProvider, onFetchClients, onFetchTestimonials]);

  // Bookings (activities) feed the Phase 4 onboarding tooltip's Booking
  // step. Also dispatched from CoachDashboardPage, but MainDashboardWrapper
  // wraps many other coach pages too, so it's fetched here as well to make
  // sure the tour has the data regardless of which page is open.
  useEffect(() => {
    if (!isProvider || !onGetAllActivities) return;
    onGetAllActivities();
  }, [isProvider, onGetAllActivities]);

  // Whether the coach has ever used messaging — feeds the Phase 4
  // onboarding tooltip's Messaging step. No stored "has messaged" flag
  // exists, so this fetches the coach's chat threads and checks if there
  // are any, mirroring the pendingInvitesCount pattern above.
  const [hasMessaged, setHasMessaged] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  useEffect(() => {
    if (!isProvider || !onFetchAllMessages) return;
    onFetchAllMessages()
      .then(res => setHasMessaged(Array.isArray(res?.data) && res.data.length > 0))
      .catch(() => setHasMessaged(false))
      .then(() => setMessagesLoaded(true));
  }, [isProvider, onFetchAllMessages]);

  // Resource library count feeds the Phase 5 onboarding tooltip below.
  useEffect(() => {
    if (!isProvider || !onFetchResources) return;
    onFetchResources();
  }, [isProvider, onFetchResources]);

  // Clients/testimonials/activities/resources are dispatched fire-and-forget
  // above (their thunks don't return a promise tied to completion), so the
  // only reliable "has this data arrived yet" signal is each duck's own
  // Fetching flag. Without this, the tour would judge Phase 3/4/5 steps as
  // incomplete using their default empty state, flash a tooltip, then hide
  // it a few seconds later once the real (already-complete) data arrives —
  // exactly the "shows then disappears in ~5 seconds" bug. We wait for a
  // fetch to actually start and finish before trusting its data.
  const sawClientsFetchingRef = useRef(false);
  const sawTestimonialsFetchingRef = useRef(false);
  const sawActivitiesFetchingRef = useRef(false);
  const sawResourcesFetchingRef = useRef(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  useEffect(() => {
    if (clientsFetching) {
      sawClientsFetchingRef.current = true;
    } else if (sawClientsFetchingRef.current) {
      setClientsLoaded(true);
    }
  }, [clientsFetching]);
  useEffect(() => {
    if (testimonialsFetching) {
      sawTestimonialsFetchingRef.current = true;
    } else if (sawTestimonialsFetchingRef.current) {
      setTestimonialsLoaded(true);
    }
  }, [testimonialsFetching]);
  useEffect(() => {
    if (activitiesFetching) {
      sawActivitiesFetchingRef.current = true;
    } else if (sawActivitiesFetchingRef.current) {
      setActivitiesLoaded(true);
    }
  }, [activitiesFetching]);
  useEffect(() => {
    if (resourcesFetching) {
      sawResourcesFetchingRef.current = true;
    } else if (sawResourcesFetchingRef.current) {
      setResourcesLoaded(true);
    }
  }, [resourcesFetching]);

  const isParticipantView =
    !forceCoachView &&
    (activeView === 'participant' || page === 'client-marketplace' || page === 'profile-view');
  // Whether the sidebar/nav should currently present as coach — true for
  // real coach accounts AND for any account that has switched into coach
  // view via the sidebar toggle below.
  const isCoachView = activeView === 'coach';

  const isVCoachTheme =
    typeof document !== 'undefined' &&
    document.body.classList.contains('v-coach-theme');
  const menuHoverBg = isVCoachTheme ? '#7C3AED' : '#0D9488';
  const menuItemStyles = {
    color: '#0F172A',
    backgroundColor: 'white',
    hover: { backgroundColor: menuHoverBg, color: '#FFFFFF' },
    active: { backgroundColor: menuHoverBg, color: '#FFFFFF' },
  };

  const emailVerified = currentUser?.attributes?.emailVerified;

  const showOnboardingTour =
    !hideSidebar &&
    (isProvider || forceCoachView) &&
    activeView === 'coach' &&
    typeof window !== 'undefined' &&
    window.innerWidth > 768;
  // Hoisted out of JSX rather than inlined as `clientsLoaded && testimonialsLoaded`
  // etc. — the CRA/babel-eslint toolchain in this repo has a parser quirk where
  // multi-operand `&&` expressions written directly as JSX attribute values
  // confuse react-hooks/rules-of-hooks and falsely flag every hook above them
  // as "called conditionally". Plain identifiers as prop values sidestep it.
  const phase3DataLoadedValue = clientsLoaded && testimonialsLoaded;
  const phase4DataLoadedValue = activitiesLoaded && messagesLoaded;

  return (
    <>
    <div className={`${css.base}${hideSidebar ? ` ${css.base_no_sidebar}` : ''}`}>
      {!hideSidebar && <div className={`${css.menu_base} ${isParticipantView ? css.menu_base_participant : ''} ${isNavOpen && css.menu_open}`}>
        <div className={css.menu_nav_top}>
          <Link to={'/s'} className={css.logo}>
            <img
              style={{ width: '100%' }}
              alt={'logo'}
              src={
                page?.startsWith('coach-') ||
                (isProvider &&
                  !location.pathname.startsWith('/v-coach/client') &&
                  !location.pathname.startsWith('/saas-chats/client') &&
                  !location.pathname.startsWith('/inbox/orders') &&
                  !location.pathname.startsWith('/employee'))
                  ? coachLockupLogo
                  : getLogoForRoute(location.pathname)
              }
            />
          </Link>
          <ChevronLeft
            className={css.menu_close_button}
            onClick={() => setIsNavOpen(false)}
          />
        </div>
        <div className={css.section_wrapper}>
          {isOrganization && activeView === 'organization' && (
              <div className={`${css.menu_section} ${css.vcorp_section}`}>
                <div
                  className={css.menu_item_wrapper}
                  onClick={() => setIsVCorpOpen(!isVCorpOpen)}
                >
                  <p>Organization</p>
                  {isVCorpOpen ? <ChevronUp /> : <ChevronDown />}
                </div>
                <Collapse isOpen={isVCorpOpen}>
                  <a
                    href={`${config.vCorpUrl}/hr/dashboard`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-dashboard' ? css.menu_item_active : ''}`}
                  >
                    <Grid />
                    <p>Dashboard</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/curriculum`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-curriculum' ? css.menu_item_active : ''}`}
                  >
                    <FileText />
                    <p>Curriculum</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/survey`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-survey' ? css.menu_item_active : ''}`}
                  >
                    <List />
                    <p>Surveys</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/coach`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-coach' ? css.menu_item_active : ''}`}
                  >
                    <User />
                    <p>Coach</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/employee`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-employees' ? css.menu_item_active : ''}`}
                  >
                    <Users />
                    <p>Employees</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/billing/budget`}
                    target="_self"
                    className={`${css.collapse_item_wrapper} ${page === 'corp-billing' ? css.menu_item_active : ''}`}
                  >
                    <CreditCard />
                    <p>Billing</p>
                  </a>
                  <a
                    href={`${config.vCorpUrl}/hr/settings`}
                    className={`${css.collapse_item_wrapper} ${page === 'corp-settings' ? css.menu_item_active : ''}`}
                  >
                    <Settings />
                    <p>Settings</p>
                  </a>
                </Collapse>
              </div>
            )}

          {activeView === 'coach' && (
            <div className={`${css.menu_section} ${css.vcoach_section}`}>
              <p className={css.section_label}>COACH</p>
                <Link
                  to={`/v-coach/dashboard`}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-dashboard-home' ? css.menu_item_active : ''}`}
                >
                  <Grid size={20} />
                  <p>Dashboard</p>
                </Link>
                <Link
                  to={`/v-coach/sessions`}
                  ref={sessionsAnchorRef}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-dashboard' ? css.menu_item_active : ''}`}
                >
                  <Video />
                  <p>Sessions</p>
                </Link>
                <Link
                  to={`/v-coach/clients`}
                  ref={clientsAnchorRef}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-clients' ? css.menu_item_active : ''}`}
                >
                  <Users size={20} />
                  <p>Clients</p>
                </Link>
                {/* Programs → Ads → Messages → Reports — grouped so the
                    sales surfaces (Programs, Ads) sit together and the
                    communication/analytics surfaces (Messages, Reports)
                    sit together. Reports is the only gated item in this
                    block — lock icon shows for Network coaches. */}
                <Link
                  to={`/v-coach/programs`}
                  ref={programAnchorRef}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-program' ? css.menu_item_active : ''}`}
                >
                  <FileText />
                  <p>Programs</p>
                </Link>
                <div
                  ref={adsAnchorRef}
                  onClick={() => requireStripe('/listings')}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-ads' ? css.menu_item_active : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <Volume2 size={20} />
                  <p>Ads</p>
                </div>
                <Link
                  to={`/saas-chats/coach`}
                  ref={messagesAnchorRef}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-messages' ? css.menu_item_active : ''}`}
                >
                  <Mail />
                  <p>Messages</p>
                </Link>
                <Link
                  to={`/v-coach/reports`}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-reports' ? css.menu_item_active : ''} ${!isVerifiedPlus ? css.sidebar_locked : ''}`}
                  title={!isVerifiedPlus ? 'Unlocked at Verified' : null}
                >
                  <PieChart size={20} />
                  <p>Reports</p>
                  {!isVerifiedPlus && <Lock size={14} className={css.sidebar_lock_icon} />}
                </Link>
                {/* Resource Archive — reusable docs/videos the coach
                    shares with clients. Available to all tiers (Network
                    is capped on count/storage/file-size); no lock icon. */}
                <Link
                  to={`/v-coach/resources`}
                  ref={resourcesAnchorRef}
                  className={`${css.collapse_item_wrapper} ${page === 'coach-resources' ? css.menu_item_active : ''}`}
                >
                  <Folder size={20} />
                  <p>Resources</p>
                </Link>
                {/* Settings — single entry into the tabbed settings
                    surface (Availability, Subscription, Payout details,
                    Account, Branding, Verification, Referrals). The
                    individual items previously lived here in the sidebar
                    but moved into Settings tabs for breathability. */}
                <Link
                  to={`/v-coach/settings`}
                  ref={settingsLinkAnchorRef}
                  className={`${css.collapse_item_wrapper} ${
                    (page === 'saas_settings' ||
                     page === 'payments' ||
                     page === 'withdrawal' ||
                     page === 'account_settings' ||
                     page === 'coach-branding' ||
                     page === 'coach-testimonials' ||
                     page === 'coach-referral') ? css.menu_item_active : ''
                  }`}
                >
                  <Settings size={20} />
                  <p>Settings</p>
                </Link>

                {/* Admin entry — only renders for Viveka admins. Backend
                    still gates the data; this just keeps the link off
                    every coach's sidebar. */}
              {isVivekaAdmin && (
                <Link
                  to={`/v-coach/admin/credentials`}
                  className={`${css.collapse_item_wrapper} ${page === 'admin-credentials' ? css.menu_item_active : ''}`}
                  style={{ marginTop: '10px', borderTop: '1px solid #F3F4F6', paddingTop: '12px' }}
                >
                  <Shield size={20} />
                  <p>Admin · Credentials</p>
                </Link>
              )}
            </div>
          )}

          {showVClient && !forceCoachView && activeView === 'participant' && (
            <div className={`${css.menu_section} ${css.vclient_section}`}>
              <p className={css.section_label}>PARTICIPANT</p>
              {currentCompany && (
                  <a
                    href={`${config.vCorpUrl}/employee/company/my-curriculums`}
                    className={css.collapse_item_wrapper}
                  >
                    <FileText />
                    <p>{currentCompany?.name || 'My Company'}</p>
                  </a>
                )}
                {/* Dashboard — participant's home base. Hero next-session
                    card, programs, mood check-in, coaches, quick actions.
                    Mirrors the coach dashboard's shape, narrowed for the
                    client side. */}
                <Link
                  to={`/v-coach/client/dashboard`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-dashboard' ? css.menu_item_active : ''}`}
                >
                  <Grid size={20} />
                  <p>Dashboard</p>
                </Link>
                {/* Sessions — full list of the participant's activities/
                    sessions across all programs. Mirrors the coach
                    sidebar's "Sessions" entry. */}
                <Link
                  to={`/v-coach/client/my-activities`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-activity' ? css.menu_item_active : ''}`}
                >
                  <Video />
                  <p>Sessions</p>
                </Link>
                {/* Coaches — full list of the participant's coaches with
                    message buttons + per-coach program breakdowns.
                    Mirrors the coach side's "Clients" entry. */}
                <Link
                  to={`/v-coach/client/coaches`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-coaches' ? css.menu_item_active : ''}`}
                >
                  <Users size={20} />
                  <p>Coaches</p>
                </Link>
                {/* Resources — documents/videos the participant's
                    coaches have shared with them. */}
                <Link
                  to={`/v-coach/client/resources`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-resources' ? css.menu_item_active : ''}`}
                >
                  <Folder size={20} />
                  <p>Resources</p>
                </Link>
                <Link
                  to={`/saas-chats/client`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-messages' ? css.menu_item_active : ''}`}
                >
                  <Mail />
                  <p>Messages</p>
                </Link>
                <Link
                  to={`/s`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-marketplace' ? css.menu_item_active : ''}`}
                >
                  <Search size={20} />
                  <p>Find A Coach</p>
                </Link>
                {/* Availability — participant connects a calendar and sets
                    weekly availability so a coach can only book times when
                    BOTH are free (two-way matching). */}
                <Link
                  to={`/v-coach/client/settings`}
                  className={`${css.collapse_item_wrapper} ${page === 'client-settings' ? css.menu_item_active : ''}`}
                >
                  <Calendar size={20} />
                  <p>Availability</p>
                </Link>
              <Link
                to={`/inbox/orders`}
                className={`${css.collapse_item_wrapper} ${page === 'coach-orders' ? css.menu_item_active : ''}`}
              >
                <CreditCard size={20} />
                <p>Purchases</p>
              </Link>
            </div>
          )}

        </div>
        {isOrganization && (
          <div className={css.sidebar_bottom}>
            {/* Organization users can have up to three views (Coach only
                when they also have a coach profile). Show a button for
                each view that ISN'T the current one, rather than a single
                two-way toggle, since there can be three destinations. */}
            {activeView !== 'organization' && (
              <button
                className={css.switch_view_btn}
                onClick={() => switchView('organization')}
              >
                <ArrowRight size={14} />
                Switch to Organization
              </button>
            )}
            {isProvider && activeView !== 'coach' && (
              <button
                className={`${css.switch_view_btn} ${css.switch_view_btn_participant}`}
                onClick={() => switchView('coach')}
              >
                <ArrowRight size={14} />
                Switch to Coach
              </button>
            )}
            {activeView !== 'participant' && (
              <button
                className={`${css.switch_view_btn} ${css.switch_view_btn_participant}`}
                onClick={() => switchView('participant')}
              >
                <ArrowRight size={14} />
                Switch to Participant
              </button>
            )}
          </div>
        )}
        {!isOrganization && (
          <div className={css.sidebar_bottom}>
            {activeView === 'coach' ? (
              <button
                className={css.switch_view_btn}
                onClick={() => switchView('participant')}
              >
                <ArrowRight size={14} />
                Switch to Participant
              </button>
            ) : (
              <button
                className={`${css.switch_view_btn} ${css.switch_view_btn_participant}`}
                onClick={() => switchView('coach')}
              >
                <ArrowRight size={14} />
                Switch to Coach
              </button>
            )}
          </div>
        )}
      </div>}

      <div className={css.content_base}>
        <div className={`${css.nav_bar} ${isParticipantView ? css.nav_bar_participant : ''} ${hideTitleOnMobile ? css.hide_title_on_mobile : ''}`}>
          {!hideSidebar && <MenuIcon className={css.menu_hamburger} onClick={() => setIsNavOpen(true)} />}
          <span className={css.nav_title}>{title}</span>
          <div className={css.nav_actions}>
            {isCoachView && (
              <div className={css.nav_icon} onClick={() => history.push('/v-coach/whats-new')} title="What's New">
                <Home size={17} />
              </div>
            )}
            <div className={css.nav_icon} onClick={() => history.push('/s')}>
              <Search size={17} />
            </div>
            {isCoachView && (
              <div className={css.nav_icon_rel} onClick={() => history.push('/inbox/sales')}>
                <Bell size={17} />
                {pendingInvitesCount > 0 && (
                  <span className={css.nav_badge}>{pendingInvitesCount}</span>
                )}
              </div>
            )}
            {!isCoachView && (
              <Menu
                direction="bottom"
                align="end"
                menuStyles={{ maxHeight: '300px', overflowY: 'auto' }}
                onMenuChange={e => {
                  if (e.open && notificationUnreadCount > 0 && onMarkAllNotificationsRead) {
                    onMarkAllNotificationsRead();
                  }
                  // Dismiss the "new notifications" tooltip the moment they
                  // open the bell, regardless of how quickly the unread
                  // count above actually clears — see NotificationTooltip
                  // for why this needs to be keyed by the newest
                  // notification's id rather than a flat seen/unseen flag.
                  if (e.open) {
                    try {
                      const userId = currentUser?.id?.uuid;
                      const newestId = notificationItems?.[0]?.id;
                      if (userId && newestId) {
                        localStorage.setItem(`${NOTIFICATION_TOOLTIP_STORAGE_PREFIX}${userId}`, newestId);
                      }
                    } catch (storageErr) {
                      // localStorage unavailable — non-fatal, worst case the
                      // tooltip shows once more next visit.
                    }
                  }
                }}
                menuButton={
                  <div className={css.nav_icon_rel} title="Notifications" ref={notificationsBellAnchorRef}>
                    <Bell size={17} />
                    {notificationUnreadCount > 0 && (
                      <span className={css.nav_badge}>{notificationUnreadCount}</span>
                    )}
                  </div>
                }
              >
                {notificationItems.length === 0 ? (
                  <MenuItem styles={menuItemStyles} disabled>
                    <div style={{ padding: '8px 4px', color: '#94A3B8', fontSize: '13px' }}>
                      No notifications yet
                    </div>
                  </MenuItem>
                ) : (
                  notificationItems.slice(0, 8).map(n => {
                    const NotifIcon = NOTIFICATION_ICON_BY_TYPE[n.type] || Bell;
                    return (
                      <MenuItem
                        key={n.id}
                        styles={{
                          ...menuItemStyles,
                          alignItems: 'flex-start',
                          whiteSpace: 'normal',
                          padding: '10px 14px',
                        }}
                        onClick={() => {
                          if (onMarkNotificationRead && !n.isRead) onMarkNotificationRead(n.id);
                          if (n.link) history.push(n.link);
                          return true;
                        }}
                      >
                        <div style={{ display: 'flex', gap: '10px', maxWidth: '280px' }}>
                          <div
                            style={{
                              flexShrink: 0,
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isVCoachTheme ? '#EDE9FE' : '#CCFBF1',
                              color: menuHoverBg,
                            }}
                          >
                            <NotifIcon size={15} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ fontWeight: n.isRead ? 500 : 700, fontSize: '13.5px' }}>
                                {n.title}
                              </div>
                              {!n.isRead && (
                                <span
                                  style={{
                                    flexShrink: 0,
                                    width: '7px',
                                    height: '7px',
                                    borderRadius: '50%',
                                    backgroundColor: menuHoverBg,
                                  }}
                                />
                              )}
                            </div>
                            {n.message && (
                              <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>
                                {n.message}
                              </div>
                            )}
                            {n.createdAt && (
                              <div style={{ fontSize: '11px', color: '#CBD5E1', marginTop: '4px' }}>
                                {timeAgo(n.createdAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      </MenuItem>
                    );
                  })
                )}
              </Menu>
            )}
            <div
              className={css.nav_icon}
              onClick={() => history.push(isCoachView ? '/saas-chats/coach' : '/saas-chats/client')}
            >
              <Mail size={17} />
            </div>
            <ReportIssueButton
              platform="coach"
              role={location.pathname.startsWith('/v-coach/client/') ? 'client' : 'coach'}
              formUrl="/support/report-issue.html"
              label="?"
              className="v-support-nav-btn"
            />
            <Menu
              direction="left"
              menuButton={
                <div className={css.avatar_wrapper} ref={profileMenuAnchorRef}>
                  {currentUser?.profileImage?.attributes?.variants['square-small']?.url ? (
                    <img
                      className={css.profile_image}
                      alt="profile"
                      src={currentUser.profileImage.attributes.variants['square-small'].url}
                    />
                  ) : (
                    <div className={css.avatar_initials}>
                      {(currentUser?.attributes?.profile?.displayName || '')
                        .split(' ').filter(Boolean).slice(0, 2)
                        .map(w => w[0].toUpperCase()).join('')}
                    </div>
                  )}
                  <span className={css.avatar_name}>
                    {currentUser?.attributes?.profile?.displayName}
                  </span>
                </div>
              }
            >
              <MenuItem styles={menuItemStyles} onClick={() => { history.push('/profile-settings'); return true; }}>Profile</MenuItem>
              <MenuItem styles={menuItemStyles} onClick={() => { history.push('/account-settings'); return true; }}>Settings</MenuItem>
              <MenuItem styles={menuItemStyles} onClick={() => { logout().then(() => history.push('/login')); return true; }}>Logout</MenuItem>
            </Menu>
          </div>
        </div>
        <div className={`${css.content} ${isParticipantView ? css.content_participant : ''}`}>
          {currentUser && !emailVerified && (
            <div className=" p-5 mb-5 bg-slate-900 text-white rounded-[4px]">
              <h2 className="text-lg leading-tight m-0 mt-1">
                Please verify your email address!{' '}
                <span className="text-lg font-normal text-gray-400">
                  ({currentUser.attributes.email})
                </span>
              </h2>
              <p>
                To complete your registration, please check your email inbox for a
                verification message from us. If you don't see it in your inbox, it might
                have landed in your spam or junk folder. Once you find it, just click the
                verification link inside, and you'll be all set. Thank you!
              </p>
              <div
                className="bg-primary w-fit px-4 py-1 rounded-sm cursor-pointer hover:bg-primary/80 transition-all text-sm"
                onClick={() => {
                  onResendVerificationEmail().then(
                    notifySuccess(`Email Resent to ${currentUser.attributes.email}`)
                  );
                }}
              >
                Resend Email
              </div>
            </div>
          )}
          {(() => {
            if (!currentUser || !page?.startsWith('coach')) return null;
            const stripeConnected = !!currentUser.attributes.stripeConnected;
            const stripeAccountData = stripeAccount?.attributes?.stripeAccountData || null;
            const hasRequirements = (type) =>
              stripeAccountData?.requirements &&
              Array.isArray(stripeAccountData.requirements[type]) &&
              stripeAccountData.requirements[type].length > 0;
            const requirementsMissing =
              hasRequirements('past_due') || hasRequirements('currently_due');
            if (stripeConnected && !requirementsMissing) return null;
            return (
              <div
                className="p-5 mb-5 bg-slate-900 text-white rounded-[4px] cursor-pointer hover:bg-slate-800 transition-all"
                onClick={() => history.push('/account/payments/withdrawal')}
              >
                <h2 className="text-lg leading-tight m-0 mt-1 flex items-center gap-2">
                  <CreditCard size={20} />
                  {stripeConnected
                    ? 'Stripe account needs verification!'
                    : 'Connect your Stripe account to receive payments!'}
                </h2>
                <p className="mt-2 mb-3 text-gray-300">
                  {stripeConnected
                    ? 'Stripe needs a few more details to verify your account before you can receive payments from clients.'
                    : 'Your Stripe payout account is not set up yet. Connect a bank account to start receiving payments.'}
                </p>
                <div className="bg-primary w-fit px-4 py-1 rounded-sm text-sm">
                  {stripeConnected ? 'Complete Verification →' : 'Set Up Stripe Account →'}
                </div>
              </div>
            );
          })()}
          {/* Order matters here:
              0. InstallmentPaymentFailedBanner — locked mid-installment (see below)
              1. SubscribeToBeVisibleBanner — tier 0 (no sub) inline reminder
              2. SubscribeToBeVisiblePopup — tier 0 (no sub) one-time modal nudge
              3. SubscriptionMigrationBanner — legacy-paid First Mover popup
              4. SubscriptionExpiryBanner — amber strip when trial/term ends soon
              Mutually exclusive in practice (tier 0 vs legacy paid vs expiring vs
              installment-locked — the last is also tier 0, but
              SubscribeToBeVisibleBanner/Popup explicitly guard themselves off
              in that case, see isInstallmentPaymentLocked) but the order keeps
              the most permanent/urgent gate first. */}
          <InstallmentPaymentFailedBanner />
          <SubscribeToBeVisibleBanner currentUser={currentUser} />
          <SubscribeToBeVisiblePopup onVisibilityChange={setSubscribePopupOpen} />
          <ProfileCompletenessChecklist currentUser={currentUser} />
          <SubscriptionMigrationBanner />
          <SubscriptionExpiryBanner />
          {/* V-coins: full-takeover minting celebration for coins earned
              since the coach last looked (e.g. a referral converted while
              they were offline). Renders nothing when there's nothing new. */}
          <VCoinCelebrationController currentUser={currentUser} />
          {children}
        </div>
      </div>
    </div>
    {showStripePopup && (
      <React.Fragment>
        <style>{`
          @keyframes stripePopupFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes stripePopupSlideIn {
            from { opacity: 0; transform: translateY(20px) scale(0.96); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes stripePopupPulseGlow {
            0%, 100% { box-shadow: 0 4px 20px rgba(20, 184, 166, 0.45), 0 0 0 0 rgba(20, 184, 166, 0.35); }
            50%       { box-shadow: 0 6px 28px rgba(20, 184, 166, 0.6),  0 0 0 8px rgba(20, 184, 166, 0); }
          }
        `}</style>
        <div
          onClick={() => setShowStripePopup(false)}
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            animation: 'stripePopupFadeIn 0.25s ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '480px',
              padding: '36px 32px',
              borderRadius: '20px',
              background:
                'radial-gradient(circle at 80% 10%, rgba(139, 92, 246, 0.25) 0%, rgba(139, 92, 246, 0) 50%), ' +
                'radial-gradient(circle at 10% 90%, rgba(20, 184, 166, 0.2) 0%, rgba(20, 184, 166, 0) 50%), ' +
                'linear-gradient(135deg, #0F172A 0%, #1A2744 60%, #0F172A 100%)',
              border: '1px solid rgba(20, 184, 166, 0.35)',
              boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
              animation: 'stripePopupSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowStripePopup(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.08)',
                color: '#FFFFFF',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            >
              <X size={18} strokeWidth={2.5} />
            </button>

            <div style={{ textAlign: 'center' }}>
              {/* Viveka-branded icon badge — purple circle (logo icon) with teal ring */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #14B8A6 100%)',
                boxShadow: '0 0 0 6px rgba(20, 184, 166, 0.15)',
                marginBottom: '20px',
              }}>
                <CreditCard size={26} color="#FFFFFF" />
              </div>
<br></br>
              {/* Label pill */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '999px',
                border: '1px solid rgba(20, 184, 166, 0.55)',
                background: 'rgba(20, 184, 166, 0.1)',
                color: '#14B8A6',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '14px',
              }}>
                Action Required
              </div>

              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 800,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}>
                Stripe Account Not Verified
              </h2>

              <p style={{
                margin: '14px auto 0',
                color: '#CBD5E1',
                fontSize: '14px',
                lineHeight: 1.65,
                maxWidth: '370px',
              }}>
                You need to verify your Stripe account before you can access this feature.
                Complete your payout setup to start receiving payments from clients.
              </p>

              <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowStripePopup(false);
                    history.push('/account/payments/withdrawal');
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    padding: '13px 32px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    letterSpacing: '0.03em',
                    animation: 'stripePopupPulseGlow 2.4s ease-in-out infinite',
                    transition: 'transform 0.18s ease, filter 0.18s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <CreditCard size={16} />
                  Verify Stripe Account
                </button>

                <button
                  type="button"
                  onClick={() => setShowStripePopup(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    color: '#94A3B8',
                    fontSize: '13px',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    )}
    {showOnboardingTour && (
      <CoachOnboardingTour
        currentUser={currentUser}
        stripeVerified={stripeVerified}
        profileAnchorRef={profileMenuAnchorRef}
        settingsAnchorRef={settingsLinkAnchorRef}
        programAnchorRef={programAnchorRef}
        adsAnchorRef={adsAnchorRef}
        clientsAnchorRef={clientsAnchorRef}
        hasClients={clients.length > 0}
        hasTestimonials={testimonials.length > 0}
        phase3DataLoaded={phase3DataLoadedValue}
        messagesAnchorRef={messagesAnchorRef}
        sessionsAnchorRef={sessionsAnchorRef}
        hasMessaged={hasMessaged}
        hasBookings={activities.length > 0}
        phase4DataLoaded={phase4DataLoadedValue}
        resourcesAnchorRef={resourcesAnchorRef}
        hasResources={resources.length > 0}
        phase5DataLoaded={resourcesLoaded}
        active={!subscribePopupOpen}
      />
    )}
    {!isCoachView && (
      <NotificationTooltip
        currentUser={currentUser}
        unreadCount={notificationUnreadCount}
        newestNotificationId={notificationItems?.[0]?.id}
        anchorRef={notificationsBellAnchorRef}
      />
    )}
    </>
  );
};

const mapStateToProps = state => {
  const { currentUser, currentUserNotificationCount, currentUserHasOrders } = state.user;
  const { unreadMessageCount } = state.socketChat;
  const { stripeAccount } = state.stripeConnectAccount;
  const { clients, clientsFetching } = state.clients;
  const { testimonials, testimonialsFetching } = state.testimonials;
  const { activities, activities_fetching: activitiesFetching } = state.activities;
  const { resources, resourcesFetching } = state.resources;
  const { items: notificationItems, unreadCount: notificationUnreadCount } = state.notifications;
  return {
    currentUser,
    currentUserNotificationCount,
    currentUserHasOrders,
    stripeAccount,
    unreadMessageCount,
    clients,
    clientsFetching,
    testimonials,
    testimonialsFetching,
    activities,
    activitiesFetching,
    resources,
    resourcesFetching,
    notificationItems,
    notificationUnreadCount,
  };
};

const mapDispatchToProps = dispatch => ({
  logout: () => dispatch(logout()),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
  onFetchPendingEnquiriesCount: () => dispatch(fetchPendingEnquiriesCount()),
  onFetchStripeAccount: () => dispatch(fetchStripeAccount()),
  onFetchClients: () => dispatch(getAllClients()),
  onFetchTestimonials: () => dispatch(fetchMyTestimonials()),
  onGetAllActivities: () => dispatch(getAllActivities()),
  onFetchAllMessages: () => dispatch(fetchAllMessages()),
  onFetchResources: () => dispatch(fetchMyResources()),
  onFetchNotifications: () => dispatch(fetchNotifications()),
  onMarkNotificationRead: (id) => dispatch(markNotificationRead(id)),
  onMarkAllNotificationsRead: () => dispatch(markAllNotificationsRead()),
});

export default withRouter(
  compose(connect(mapStateToProps, mapDispatchToProps))(MainDashboardWrapper)
);
